import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { Avatar } from '../components/Avatar';
import { Say } from '../components/Say';
import { FilmTable } from '../components/FilmTable';
import { CrewHome } from '../components/CrewHome';
import { StoryboardTeaser } from '../components/StoryboardTeaser';
import { OpenPlay } from '../components/ExerciseTile';
import { useLightbox } from '../components/Lightbox';
import { CREW, greeting, pendingMeet } from '../data/crew';
import { loadProfile, saveProfile, currentDay, madeToday, daysSinceMade } from '../lib/state';
import { listMyPosts, type FeedPost } from '../lib/feed';
import { listCharacters, getFilm, type Inspiration, type Character, type FilmStatus } from '../lib/crew';
import { collectPieces, currentPieces, picksToSave, toPick, filmKicker, type FilmPiece } from '../lib/pieces';
import { SLOTS, filmReady, nextGap, type Slot } from '../data/film';
import type { Path } from '../data/types';
import { track } from '../analytics';
import { PrintTeaser } from '../components/Reveal';
import { heldPrints, withoutHeld } from '../lib/reveal';
import { useViewed } from '../lib/viewed';

/** The newest thing the crew drew, for the top of Home when it is recent (K34 N-05). */
interface Fresh { url: string; note: string; day: number; at: number }
const FRESH_MS = 48 * 3600 * 1000;

function newest(posts: FeedPost[], chars: Character[]): Fresh | null {
  const out: Fresh[] = [];
  for (const p of posts) for (const r of p.renders || []) out.push({ url: r.url, note: `from your Day ${p.day} idea: “${r.idea}”`, day: p.day, at: p.createdAt });
  for (const c of chars) if (c.imageUrl) out.push({ url: c.imageUrl, note: c.name ? `${c.name} · from your words` : `from your words: “${c.text}”`, day: 0, at: c.createdAt || 0 });
  out.sort((a, b) => b.at - a.at);
  const top = out[0];
  return top && Date.now() - top.at < FRESH_MS ? top : null;
}

/**
 * A story told in the last two days, whatever became of it: storyboarded, on
 * its way, or not come out. One that failed or ran short still shows, so a
 * story is never lost without a word (2026-09-28: one failed unseen).
 */
function recentStory(posts: FeedPost[]): FeedPost | undefined {
  return posts.find((p) => !!p.storyId && !!p.storyboardStatus && Date.now() - p.createdAt < FRESH_MS);
}

/**
 * Today (core loop doc 20 §4, K34 N-05; trimmed 2026-09-29). What the crew did
 * since you left — a print to turn over, their newest picture, a story's
 * storyboard — then the film as it stands, its next gap lit, one tap from the
 * table; below it, quietly, open play (a blank page). The exercises live on
 * the Road and the posts on the Feed, so neither is repeated here.
 *
 * Count-up, never calendar; no gap in days is ever shown. On a return after
 * three or more days the headline says creativity is where you left it.
 */
export function Home() {
  useViewed('home');
  const nav = useNavigate();
  const [profile, setProfile] = useState(loadProfile);
  const day = currentDay(profile);
  const done = madeToday(profile);
  const away = daysSinceMade(profile);
  const returning = profile.madeDays > 0 && away >= 3;
  const knock = pendingMeet(profile.madeDays, profile.met);
  const g = greeting(profile.anchor, profile.name);
  const { open, lightbox } = useLightbox();

  const [posts, setPosts] = useState<FeedPost[]>([]);
  const [chars, setChars] = useState<Character[]>([]);
  const [kept, setKept] = useState<Inspiration[]>([]);
  const [keptLoaded, setKeptLoaded] = useState(false);
  const [loads, setLoads] = useState(0);
  const [filmState, setFilmState] = useState<FilmStatus | null>(null);
  useEffect(() => {
    listMyPosts(40).then(setPosts).catch(() => {}).finally(() => setLoads((n) => n + 1));
    listCharacters().then(setChars).catch(() => {}).finally(() => setLoads((n) => n + 1));
    getFilm().then(setFilmState).catch(() => {});
  }, []);
  // every source of pieces has answered: only then may the table write its seats down
  const loaded = loads >= 2 && keptLoaded;
  const rolled = filmState?.film ?? null;

  // The table (K33): everything you have made, and what sits in each slot now.
  // a picture not yet turned over is a print, and nothing shows it until you do (well/product/23 §4)
  const prints = useMemo(() => heldPrints(posts), [posts]);
  const seen = useMemo(() => withoutHeld(posts), [posts]);
  const collection = useMemo(() => collectPieces(kept, chars, seen, profile.pencilled), [kept, chars, seen, profile.pencilled]);
  const picks = profile.film?.picks;
  const current = useMemo(() => currentPieces(collection, picks), [collection, picks]);
  const filled = useMemo(() => new Set<string>(SLOTS.filter((s) => current[s])), [current]);
  // The row on the table is the film the backend rolls: once every source is in, write each seat down.
  useEffect(() => {
    if (!loaded) return;
    const p = loadProfile();
    const next = picksToSave(current, p.film?.picks);
    if (next) saveProfile({ ...p, film: { ...(p.film || {}), picks: next } });
  }, [current, loaded]);
  const seat = (slot: Slot, piece: FilmPiece) => {
    const p = loadProfile();
    const np = { ...p, film: { ...(p.film || {}), picks: { ...(p.film?.picks || {}), [slot]: toPick(piece) } } };
    saveProfile(np); setProfile(np);
  };
  const ready = filmReady(filled);
  const gap = nextGap(filled);
  const fresh = useMemo(() => newest(seen, chars), [seen, chars]);
  // A story told in the last two days, storyboarded or on its way (research 16, C1).
  const story = useMemo(() => recentStory(posts), [posts]);
  // The film row is the one way to the table; once rolled it says so, where the roll tile used to.
  const kicker = rolled?.status === 'pending' ? 'Your first film · rolling' : rolled?.status === 'done' && ready ? 'Your first film · rolled, watch it' : filmKicker(current);

  const setPath = (path: Path) => { const p = { ...profile, path }; saveProfile(p); setProfile(p); };

  return (
    <Frame tabs left={<span className="tag">Day {day}</span>}>
      <div className="row between" style={{ alignItems: 'flex-start' }}>
        <div className="stack" style={{ gap: 4 }}>
          <span className="label">{(prints.length || fresh) && !returning ? 'While you were away' : g.moment}</span>
          <h1>{returning ? 'Creativity is where you left it.' : prints.length ? 'Something’s dry.' : fresh ? 'Your crew drew this.' : done ? 'Made today.' : g.line}</h1>
        </div>
      </div>

      {profile.madeDays === 0 && (
        <div className="chips">
          {(['writing', 'drawing'] as Path[]).map((p) => (
            <button key={p} className={`chip ${profile.path === p ? 'on' : ''}`} onClick={() => setPath(p)}>{p === 'writing' ? 'Writing' : 'Drawing'}</button>
          ))}
          <button className="chip" disabled>Audio · from Day 13</button>
        </div>
      )}

      {knock && (
        <button className="knock" style={{ border: 0, cursor: 'pointer', textAlign: 'left', font: 'inherit', color: 'inherit' }} onClick={() => nav('/feed')}>
          <div className="row" style={{ gap: 10 }}><Avatar who={knock} size={36} /><div className="stack" style={{ gap: 0 }}><span className="label" style={{ color: 'var(--butter-dark)' }}>Someone at the door of your feed</span><b>{CREW[knock].name} wants to say hello.</b></div></div>
        </button>
      )}

      {/* A print waiting to be turned over comes before anything else (K38 R6). */}
      {prints.length > 0 && (
        <PrintTeaser day={prints[0].day} words={prints[0].idea} count={prints.length} onTurn={() => { track('print_open_from', { from: 'home', count: prints.length }); nav(`/print/${prints[0].postId}`, { viewTransition: true }); }} />
      )}

      {/* What changed: the crew's newest picture, big, while it is new (N-05). */}
      {fresh && !returning && !prints.length && (
        <div className="sheet hero-sheet">
          <img className="hero-img" src={fresh.url} alt={fresh.note} onClick={() => open(fresh.url, fresh.note)} />
          <div className="tagrow" style={{ padding: '10px 14px 12px' }}><span className="tag-crew">Drawn by your crew · {fresh.note}</span></div>
        </div>
      )}

      {story && <StoryboardTeaser postId={story.postId} />}

      {/* The film as it stands, the next gap lit. */}
      <FilmTable mode="compact" pieces={collection} current={current} onPick={seat} next={ready ? null : gap} kicker={kicker} onOpen={() => { track('film_table_open', { from: 'home' }); nav('/film'); }} />

      <CrewHome onKeptChange={(k) => { setKept(k); setKeptLoaded(true); }} />

      <OpenPlay onPick={() => { track('home_pick', { challengeId: 'pages', how: 'pages' }); nav('/do/pages', { state: { door: 'front' } }); }} />

      <Say where="home" day={day} />
      {lightbox}
    </Frame>
  );
}
